import { useState, useEffect, useMemo } from 'react';
import { FlaskConical, Search } from 'lucide-react';
import cx from 'classnames';
import { useTestingApi } from '../hooks/useTestingApi';
import { ITestDefinition } from '../../models/testDefinition';
import { ITeam } from '../../models/Team';
import { IEnabledTest } from '../../models/teamTestConfig';
import { useEndpoints } from '../../hooks/useEndpoints';
import { queryClient } from '../../constants/queryClient';
import GenericModalV2 from '../../shared/GenericModal/GenericModal';
import CustomButton from '../../shared/CustomButton/CustomButton';
import Typhography from '../../shared/Typhography/Typhography';
import toast from 'react-hot-toast';
import TestConfigItem from './TestConfigItem';
import styles from './TestConfigModal.module.scss';

interface TestConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: ITeam<any>;
}

const categoryLabels: Record<string, string> = {
  forza: 'Forza',
  salto: 'Salto',
  velocita: 'Velocità',
  resistenza: 'Resistenza',
  mobilita: 'Mobilità',
  composizione: 'Composizione Corporea',
  rtp_rts: 'RTP/RTS',
};

const categoryOrder = ['forza', 'salto', 'velocita', 'resistenza', 'mobilita', 'composizione', 'rtp_rts'];

export default function TestConfigModal({ isOpen, onClose, team }: TestConfigModalProps) {
  const [localEnabledTests, setLocalEnabledTests] = useState<IEnabledTest[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { catalog, teamConfig } = useTestingApi();
  const { updateTeamTestConfig } = useEndpoints();

  useEffect(() => {
    if (isOpen) {
      // Carica da teamConfig (GET /testing/config/:teamId) o fallback a team.enabledTests
      const configTests: IEnabledTest[] = teamConfig?.enabledTests ?? [];
      if (configTests.length > 0) {
        setLocalEnabledTests(configTests);
      } else {
        // Legacy: team.enabledTests potrebbe essere string[] — normalizza
        const raw: any[] = (team as any).enabledTests ?? [];
        const normalized: IEnabledTest[] = raw
          .map((t: any) => {
            if (typeof t === 'string') {
              const def = catalog.find((c: ITestDefinition) => c._id === t || c.id === t);
              return { testId: def?.id ?? t, unit: def?.defaultUnit ?? '' };
            }
            return { testId: t.testId ?? t.id ?? t._id, unit: t.unit ?? '' };
          })
          .filter((t) => t.testId);
        setLocalEnabledTests(normalized);
      }
      setSearchQuery('');
      setActiveCategory(null);
    }
  }, [isOpen, team, teamConfig, catalog]);

  // ─── Lookup abilitazione ──────────────────────────────────────────────────

  const isTestEnabled = (test: ITestDefinition) =>
    localEnabledTests.some((t) => t.testId === test.id || t.testId === test._id);

  const getEnabledTest = (test: ITestDefinition): IEnabledTest | undefined =>
    localEnabledTests.find((t) => t.testId === test.id || t.testId === test._id);

  // ─── buildEnabledTest — preserva unitLabels esistenti su re-toggle ────────

  const buildEnabledTest = (t: ITestDefinition, existing?: IEnabledTest): IEnabledTest => ({
    testId: t.id,
    unit: t.defaultUnit,
    ...(t.exerciseId && { exerciseId: t.exerciseId }),
    ...(t.allowedUnits && t.allowedUnits.length > 1 && { units: t.allowedUnits }),
    ...(t.outputMetrics && t.outputMetrics.length > 1 && { metrics: [...t.outputMetrics] }),
    // Preserva le label personalizzate già impostate (fix re-toggle / selectAll)
    ...(existing?.unitLabels && Object.keys(existing.unitLabels).length > 0
      ? { unitLabels: existing.unitLabels }
      : {}),
  });

  // ─── Toggle test (abilita/disabilita) ────────────────────────────────────

  const toggleTest = (test: ITestDefinition) => {
    setLocalEnabledTests((prev) => {
      const existing = prev.find((t) => t.testId === test.id || t.testId === test._id);
      if (existing) {
        return prev.filter((t) => t.testId !== test.id && t.testId !== test._id);
      }
      return [...prev, buildEnabledTest(test, undefined)];
    });
  };

  // ─── Toggle singola metrica per test multi-metrica (sez.2.2) ─────────────

  const toggleMetric = (test: ITestDefinition, metric: string) => {
    setLocalEnabledTests((prev) =>
      prev.map((t) => {
        if (t.testId !== test.id) return t;
        const current = t.metrics ?? test.outputMetrics ?? [];
        const next = current.includes(metric)
          ? current.filter((m) => m !== metric)
          : [...current, metric];
        // Almeno una metrica deve restare abilitata
        return { ...t, metrics: next.length > 0 ? next : current };
      }),
    );
  };

  // ─── Aggiorna label personalizzata per un'unità specifica ─────────────────
  // Se la label è vuota → elimina la chiave; se l'intero oggetto è vuoto → undefined

  const updateUnitLabel = (testId: string, unit: string, label: string) => {
    setLocalEnabledTests((prev) =>
      prev.map((t) => {
        if (t.testId !== testId) return t;

        const current: Record<string, string> = { ...(t.unitLabels ?? {}) };

        if (label.trim() === '') {
          delete current[unit];
        } else {
          current[unit] = label;
        }

        const hasLabels = Object.keys(current).length > 0;
        return {
          ...t,
          unitLabels: hasLabels ? current : undefined,
        };
      }),
    );
  };

  // ─── Seleziona/Deseleziona tutti (preserva unitLabels tramite lookup prev) ─

  const selectAll = (tests: ITestDefinition[]) => {
    setLocalEnabledTests((prev) => {
      const existing = new Map(prev.map((t) => [t.testId, t]));
      const toAdd = tests
        .filter((t) => !existing.has(t.id) && !existing.has(t._id))
        .map((t) => buildEnabledTest(t, existing.get(t.id) ?? existing.get(t._id)));
      return [...prev, ...toAdd];
    });
  };

  const deselectAll = (tests: ITestDefinition[]) => {
    const toRemove = new Set(tests.flatMap((t) => [t.id, t._id]));
    setLocalEnabledTests((prev) => prev.filter((t) => !toRemove.has(t.testId)));
  };

  // ─── Salvataggio ─────────────────────────────────────────────────────────

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateTeamTestConfig(team._id, { enabledTests: localEnabledTests });
      queryClient.invalidateQueries({ queryKey: ['testing-config', team._id] });
      queryClient.invalidateQueries({ queryKey: ['user', team._id] });
      toast.success('Test aggiornati');
      onClose();
    } catch {
      toast.error('Errore nel salvataggio');
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Raggruppamento catalogo per categoria ────────────────────────────────

  const groupedCatalog = useMemo(() => {
    const filtered = catalog.filter(
      (test: ITestDefinition) =>
        !test.readinessOnly &&
        test.name.toLowerCase().includes(searchQuery.toLowerCase()),
    );
    const groups: Record<string, ITestDefinition[]> = {};
    filtered.forEach((test: ITestDefinition) => {
      if (!groups[test.category]) groups[test.category] = [];
      groups[test.category].push(test);
    });
    return groups;
  }, [catalog, searchQuery]);

  const sortedCategories = useMemo(
    () => categoryOrder.filter((c) => groupedCatalog[c]),
    [groupedCatalog],
  );

  const enabledCount = localEnabledTests.length;
  const totalCount = catalog.length;

  // Conteggio abilitati per categoria (per le pill)
  const enabledByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    catalog.forEach((test: ITestDefinition) => {
      if (!counts[test.category]) counts[test.category] = 0;
      if (isTestEnabled(test)) counts[test.category]++;
    });
    return counts;
  }, [catalog, localEnabledTests]);

  const visibleCategories = activeCategory
    ? sortedCategories.filter((c) => c === activeCategory)
    : sortedCategories;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <GenericModalV2
      isOpen={isOpen}
      onClose={onClose}
      title="Configura test"
      size="xl"
      isLoading={isSaving}
      Footer={
        <div className={styles.footerRow}>
          <div className={styles.footerCount}>
            <FlaskConical size={14} />
            <span>
              {enabledCount} / {totalCount} test abilitati
            </span>
          </div>
          <div className={styles.footerActions}>
            <CustomButton color="outline" onClick={onClose} disabled={isSaving}>
              Annulla
            </CustomButton>
            <CustomButton color="secondary" onClick={handleSave} isLoading={isSaving}>
              Salva
            </CustomButton>
          </div>
        </div>
      }
    >
      <div className={styles.root}>
        {/* Barra di ricerca */}
        <div className={styles.searchWrapper}>
          <Search className={styles.searchIcon} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca test..."
            className={styles.searchInput}
          />
        </div>

        {/* Pill categorie */}
        <div className={styles.categoryPills}>
          <button
            className={cx(styles.categoryPill, !activeCategory && styles.categoryPillActive)}
            onClick={() => setActiveCategory(null)}
          >
            Tutti
            <span className={styles.pillCount}>{enabledCount}</span>
          </button>
          {sortedCategories.map((cat) => (
            <button
              key={cat}
              className={cx(styles.categoryPill, activeCategory === cat && styles.categoryPillActive)}
              onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
            >
              {categoryLabels[cat] ?? cat}
              {(enabledByCategory[cat] ?? 0) > 0 && (
                <span className={styles.pillCount}>{enabledByCategory[cat]}</span>
              )}
            </button>
          ))}
        </div>

        {/* Lista test raggruppata per categoria */}
        <div className={styles.testListScroll}>
          {visibleCategories.map((category) => {
            const tests = groupedCatalog[category];
            const allEnabled = tests.every((t) => isTestEnabled(t));
            return (
              <div key={category} className={styles.categorySection}>
                <div className={styles.categoryHeader}>
                  <Typhography size="bold-12" className="color-text-1">
                    {categoryLabels[category] ?? category}
                  </Typhography>
                  <button
                    className={styles.selectAllBtn}
                    onClick={() => (allEnabled ? deselectAll(tests) : selectAll(tests))}
                  >
                    {allEnabled ? 'Deseleziona tutti' : 'Seleziona tutti'}
                  </button>
                </div>

                <div className={styles.testGrid}>
                  {tests.map((test: ITestDefinition) => (
                    <TestConfigItem
                      key={test._id}
                      test={test}
                      enabled={isTestEnabled(test)}
                      enabledTest={getEnabledTest(test)}
                      onToggle={() => toggleTest(test)}
                      onToggleMetric={(metric) => toggleMetric(test, metric)}
                      onUnitLabelChange={(unit, label) =>
                        updateUnitLabel(test.id, unit, label)
                      }
                    />
                  ))}
                </div>
              </div>
            );
          })}

          {sortedCategories.length === 0 && (
            <div className={styles.emptyState}>
              <Typhography size="regular-14" className="color-text-3">
                {catalog.length === 0 ? 'Caricamento catalogo...' : 'Nessun test trovato'}
              </Typhography>
            </div>
          )}
        </div>
      </div>
    </GenericModalV2>
  );
}
